import React from 'react';
import { connect } from 'react-redux';
import { unstable_HistoryRouter as HistoryRouter } from 'react-router';

import { RootState } from 'MyTypes';
import { pick } from 'lodash';

import { parseApiError } from '@portal/common/api/request';
import { BodyText3, Link } from '@portal/common/components';
import { getMemberFullName } from '@portal/common/models/models-helpers';
import { captureException, captureUnhandledError } from '@portal/common/services/error-tracking';
import { localStorageAdapter } from '@portal/common/services/local-storage';
import AppStateProvider from '@portal/common/services/local-storage/AppStateProvider';
import { showSentryReportDialog } from '@portal/common/services/sentry';
import { styled } from '@portal/common/theme';
import {
    IMPERSONATED_SESSION,
    REDIRECTION_SESSION,
    getCookie,
} from '@portal/common/utility/cookies';

import api from '../../api';
import config from '../../config';
import Router from '../../router';
import RouterListener from '../../router/RouterListener';
import { createStore } from '../../store';
import history from '../../store/history';
import * as organizationActions from '../../store/organization/actions';
import * as sessionActions from '../../store/session/actions';
import { getSession, getSessionAccount, getSessionAccountId } from '../../store/session/selectors';
import AppNotifications from '../AppNotifications';

const ImpersonateInfo = styled('div')(({ theme }) => ({
    width: '100%',
    background: theme.color.red,
    color: theme.color.white,
    padding: '8px 20px',
}));

const mapStateToProps = (state: RootState) => ({
    accountId: getSessionAccountId(state),
    sessionAccount: getSessionAccount(state),
    session: getSession(state),
});

const dispatchProps = {
    destroySession: sessionActions.destroySession,
    loadAccount: sessionActions.loadAccount,
    selectOrganization: organizationActions.selectOrganization,
};

type Props = ReturnType<typeof mapStateToProps> & typeof dispatchProps;
type State = { error: object | null; eventId?: string };

class Root extends React.Component<Props, State> {
    state: State = { error: null };

    componentDidMount() {
        const { accountId, destroySession, loadAccount, selectOrganization } = this.props;

        // set global error handler
        api.setGlobalErrorHandler((error) => {
            const apiError = parseApiError(error);

            // if invalid session destroy the user session
            if (apiError?.code === 401 && apiError?.message === 'Invalid session') {
                destroySession({ error: 'Session expired. Please Sign In again.' });
            } else if (apiError?.code === 403) {
                // if forbidden (403) error, logout the user
                destroySession({
                    error: 'Access forbidden due to commercial licensing agreement with IHME has expired.',
                });
            } else {
                captureException(error);
            }

            // it should always throw parsedError to be then catched by consumer handler
            // because as global handler is invoked before consumer then handler
            throw apiError;
        });

        if (accountId) {
            // Load the latest account info, validate the session, and actualize the organization data
            loadAccount().then(({ organization }) => selectOrganization(organization));
        }
    }

    componentDidCatch(error, info) {
        // console.log('Unhandled component error', error, info);
        this.setState({ error });
        const callback = (eventId: string) => {
            this.setState({ eventId });
        };
        captureUnhandledError(error, info, callback);
    }

    render() {
        const { accountId, sessionAccount, session, destroySession } = this.props;

        if (accountId && sessionAccount == null) {
            return null;
        }

        const renderImpersonationsInfo = () => {
            if (session.isImpersonated && session.account) {
                const { organization } = session.account;
                const fullName = getMemberFullName(session.account);
                const to = config.staffUrl + 'organizations/' + organization.id + '/members';
                return (
                    <ImpersonateInfo>
                        <BodyText3>
                            You are temporarily logged in as {fullName}. When you're done,{' '}
                            <Link
                                style={{
                                    color: 'white',
                                    textDecoration: 'underline',
                                }}
                                href={to}
                                onClick={(ev) => {
                                    destroySession();
                                }}
                            >
                                switch back
                            </Link>{' '}
                            to your account
                        </BodyText3>
                    </ImpersonateInfo>
                );
            }
            return null;
        };

        if (this.state.error) {
            // render fallback UI
            return (
                <button
                    type="button"
                    onClick={() => showSentryReportDialog({ eventId: this.state.eventId })}
                >
                    Report feedback
                </button>
            );
        } else {
            return (
                <>
                    {renderImpersonationsInfo()}
                    <AppNotifications />
                    <Router />
                </>
            );
        }
    }
}

const ConnectedRoot = connect(mapStateToProps, dispatchProps)(Root);

const ConnectedRootWithStateProvider = () => {
    const impersonatedSession = getCookie(IMPERSONATED_SESSION);
    const redirectionSession = getCookie(REDIRECTION_SESSION);

    return (
        <AppStateProvider<RootState>
            version={config.storageStateVersion}
            stateKey="redux"
            storageAdapter={localStorageAdapter}
            createStore={(initialState) => {
                const state = impersonatedSession
                    ? {
                          session: {
                              ...impersonatedSession,
                              isImpersonated: !!impersonatedSession,
                          },
                          organization: impersonatedSession.account.organization,
                      }
                    : redirectionSession
                      ? redirectionSession
                      : initialState;

                return createStore(state);
            }}
            onInitialState={({ state }) => {
                if (impersonatedSession) {
                    api.setSessionToken(impersonatedSession.token);
                } else if (state && state.session && state.session.token) {
                    api.setSessionToken(state.session.token);
                }
            }}
            onStateUpdate={({ state, setState }) => {
                setState(pick(state, ['session', 'organization', 'userSettings']));
            }}
        >
            <HistoryRouter
                history={history}
                unstable_useTransitions={false}
                future={{ v7_relativeSplatPath: true }}
            >
                <RouterListener />
                <ConnectedRoot />
            </HistoryRouter>
        </AppStateProvider>
    );
};

export default ConnectedRootWithStateProvider;
